import React, { useState, useEffect } from 'react';
import { Col, Container, Row, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import { usePasswordSettings } from './hooks';
import Button from '../common/button';
import { toast } from 'react-toastify';
import { Loader } from 'semantic-ui-react';
import 'react-toastify/dist/ReactToastify.css';
import PasswordSettingsSkeleton from './Skeletons/PasswordSettingsSkeleton';
import ToastMessage from '../common/ToastMessage';
import GenericButton from 'src/Components/GenericButton/GenericButton';

const PasswordSettings = () => {
  const {
    schema,
    formValues,
    updatedFormValues,
    loader,
    skeletonLoader,
    handleCheckBox,
    handleInput,
    savePasswordSettings,
  } = usePasswordSettings('passwordSetting');
  const [valuesChanged, setValueChanged] = useState(false);
  const SuccessMessage = () => (
    <ToastMessage
      type="success"
      title="Password Settings."
      subTitle="Password Settings has been saved successfully."
    />
  );

  const ErrorMessage = () => (
    <ToastMessage
      type="error"
      title="Password Settings."
      subTitle=" Error occurred while saving Password Settings"
    />
  );

  return (
    <Container className="password-setting-container">
      <Row>
        <Col sm={12} className="p-0">
          <h2 className="pb-3 mb-4">Password Setting</h2>
        </Col>
        <Col sm={12} className="p-0">
          <p className="mb-4">
            Set up the Password Policy by selecting a combination of the
            options.
          </p>
          <Formik
            validationSchema={schema}
            initialValues={{
              allowUnlockDisabledLogin: formValues.allowUnlockDisabledLogin,
              passwordMinLength: formValues.passwordMinLength,
              passwordMaxLength: formValues.passwordMaxLength,
              invalidAttempts: formValues.invalidAttempts,
              numberOfUpperCase: formValues.numberOfUpperCase,
              numberOfNumerics: formValues.numberOfNumerics,
              numberOfSpecialCharacters: formValues.numberOfSpecialCharacters,
            }}
            enableReinitialize
            onSubmit={(values, actions) => {
              actions.setStatus(undefined);
              savePasswordSettings(values)
                .then(() => {
                  toast.success(<SuccessMessage />, setValueChanged(false));
                })
                .catch((error) => {
                  toast.error(<ErrorMessage />, setValueChanged(false));
                });
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              isValid,
              setFieldValue,
            }) =>
              skeletonLoader ? (
                <PasswordSettingsSkeleton />
              ) : (
                <>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col>
                        <Form.Group
                          controlId="allowUnlockDisabledLogin"
                          className="mb-4"
                        >
                          <Form.Check
                            type="checkbox"
                            label="Allow Users to unlock the disabled login"
                            checked={updatedFormValues.allowUnlockDisabledLogin}
                            onChange={(e) => {
                              handleCheckBox(e);
                              setValueChanged(true);
                            }}
                            name="allowUnlockDisabledLogin"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col sm={4}>
                        <Form.Group
                          controlId="passwordMinLength"
                          className="mb-4"
                        >
                          <Form.Label for="MinLength" className="m-0">
                            Minimum Character Allowed
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={updatedFormValues.passwordMinLength}
                            onChange={(e) => {
                              handleInput(e, setFieldValue, handleChange);
                              setValueChanged(true);
                            }}
                            onBlur={(e) => {
                              handleInput(e, setFieldValue, handleBlur);
                            }}
                            name="passwordMinLength"
                            isInvalid={
                              touched.passwordMinLength &&
                              errors.passwordMinLength
                            }
                          />
                          <Form.Control.Feedback
                            type="invalid"
                            className="mt-1"
                          >
                            <span role="alert">
                              {touched.passwordMinLength &&
                                errors.passwordMinLength}
                            </span>
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col sm={4}>
                        <Form.Group
                          controlId="passwordMaxLength"
                          className="mb-4"
                        >
                          <Form.Label for="MaxLength" className="m-0">
                            Maximum Character Allowed
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={updatedFormValues.passwordMaxLength}
                            onChange={(e) => {
                              handleInput(e, setFieldValue, handleChange);
                              setValueChanged(true);
                            }}
                            onBlur={(e) => {
                              handleInput(e, setFieldValue, handleBlur);
                            }}
                            name="passwordMaxLength"
                            isInvalid={
                              touched.passwordMaxLength &&
                              errors.passwordMaxLength
                            }
                          />
                          <Form.Control.Feedback
                            type="invalid"
                            className="mt-1"
                          >
                            <span role="alert">
                              {touched.passwordMaxLength &&
                                errors.passwordMaxLength}
                            </span>
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col sm={4}>
                        <Form.Group
                          controlId="invalidAttempts"
                          className="mb-4"
                        >
                          <Form.Label for="InvalidAttempts" className="m-0">
                            Maximum Number of Attempt(s)
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={updatedFormValues.invalidAttempts}
                            onChange={(e) => {
                              handleInput(e, setFieldValue, handleChange);
                              setValueChanged(true);
                            }}
                            onBlur={(e) =>
                              handleInput(e, setFieldValue, handleBlur)
                            }
                            name="invalidAttempts"
                            isInvalid={
                              touched.invalidAttempts && errors.invalidAttempts
                            }
                          />
                          <Form.Control.Feedback
                            type="invalid"
                            className="mt-1"
                          >
                            <span role="alert">
                              {touched.invalidAttempts &&
                                errors.invalidAttempts}
                            </span>
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <hr />

                    <Row>
                      <Col sm={12}>
                        <h4 className="mt-4 mb-4">Password Rules</h4>
                      </Col>
                      <Col>
                        <Form.Group
                          controlId="numberOfUpperCase"
                          className="mb-4"
                        >
                          <Form.Check
                            type="checkbox"
                            label="At least 1 upper case character (a-z)"
                            checked={updatedFormValues.numberOfUpperCase}
                            onChange={(e) => {
                              handleCheckBox(e);
                              setValueChanged(true);
                            }}
                            name="numberOfUpperCase"
                          />
                        </Form.Group>
                        <Form.Group controlId="NumericsReqd" className="mb-4">
                          <Form.Check
                            type="checkbox"
                            label="At least 1 number (0-9)"
                            checked={updatedFormValues.numberOfNumerics}
                            onChange={(e) => {
                              handleCheckBox(e);
                              setValueChanged(true);
                            }}
                            name="numberOfNumerics"
                          />
                        </Form.Group>
                        <Form.Group controlId="CharactersReqd" className="mb-4">
                          <Form.Check
                            type="checkbox"
                            label="At least 1 symbol (Example: ~!@$)"
                            checked={
                              updatedFormValues.numberOfSpecialCharacters
                            }
                            onChange={(e) => {
                              handleCheckBox(e);
                              setValueChanged(true);
                            }}
                            name="numberOfSpecialCharacters"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col>
                        <GenericButton
                          type="submit"
                          disable={!valuesChanged || loader}
                          buttonDisabled={!valuesChanged}
                        >
                          Save
                        </GenericButton>
                      </Col>
                    </Row>
                    {/*{loader && (
                      <div className="custom-loader mt-0 mt-md-3 mt-lg-4 d-flex justify-content-start">
                        <div className="loader-icon position-relative mr-4">
                          <Loader
                            className="text-dark font-weight-bold loader-color marginTop-Login-loader"
                            size="small"
                            active
                          />
                        </div>
                        <div className="text-dark font-weight-bold">Saving settings...</div>
                      </div>
                    )} */}
                  </Form>
                </>
              )
            }
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};

export default PasswordSettings;
